import React, { useMemo, useState } from 'react'
import {
  Box, Chip, Collapse, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from '@mui/material'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import TrendingUpIcon   from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import SpeedIcon from '@mui/icons-material/Speed'
import { PRIMARY, PRIMARY_LIGHT, PRIMARY_MID, WARNING, WARNING_LIGHT, GREEN, RED, scopeTotals, Kpi, Pct } from './index'

const nf = n => (n == null || isNaN(n)) ? '—' : Math.round(n).toLocaleString('en-IN')

function SegBadge({ seg }) {
  return (
    <Box component='span' sx={{ fontSize: '0.6rem', fontWeight: 800, px: 0.8, py: 0.25, borderRadius: 0.5, letterSpacing: 0.3, background: seg === 'ENT' ? PRIMARY_LIGHT : WARNING_LIGHT, color: seg === 'ENT' ? PRIMARY : WARNING }}>
      {seg}
    </Box>
  )
}

function Th({ children, col, sortCol, sortDir, onSort, right }) {
  const active = sortCol === col
  return (
    <TableCell align={right ? 'right' : 'left'} onClick={col ? () => onSort(col) : undefined} sx={{
      fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: active ? PRIMARY : '#a0a0b8',
      background: '#F4F6F8', borderBottom: `2px solid ${PRIMARY_MID}`, py: 1.25, whiteSpace: 'nowrap',
      cursor: col ? 'pointer' : 'default', position: 'sticky', top: 0, zIndex: 2,
      '&:hover': col ? { color: PRIMARY } : {},
    }}>
      {children}{active && <span style={{ fontSize: 8, marginLeft: 3 }}>{sortDir === 'asc' ? '▲' : '▼'}</span>}
    </TableCell>
  )
}

function GoalChip({ g }) {
  if (g == null || isNaN(g)) return null
  if (g >= 10) return <Chip label='On track' size='small' sx={{ height: 18, fontSize: '0.58rem', fontWeight: 700, background: '#dcfce7', color: '#166534', borderRadius: '6px' }} />
  if (g >= 0)  return <Chip label='At risk'  size='small' sx={{ height: 18, fontSize: '0.58rem', fontWeight: 700, background: '#fef9c3', color: '#854d0e', borderRadius: '6px' }} />
  return           <Chip label='Behind'    size='small' sx={{ height: 18, fontSize: '0.58rem', fontWeight: 700, background: '#FFE7D9', color: '#B72136', borderRadius: '6px' }} />
}

function DrillContent({ s, L0, L1, L2, S1, S2 }) {
  const mini = (title, rows, isGrowth) => {
    if (!rows?.length) return null
    const accent = isGrowth ? GREEN : RED
    return (
      <Box sx={{ mt: 2 }}>
        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: accent, mb: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {isGrowth ? <TrendingUpIcon sx={{ fontSize: 13 }} /> : <TrendingDownIcon sx={{ fontSize: 13 }} />}
          {title}
        </Typography>
        <Table size='small' sx={{ '& td, & th': { py: 0.75, px: 1.25 } }}>
          <TableHead>
            <TableRow sx={{ background: accent + '10' }}>
              {['Account', 'Seg', L0, L1, `${L2} MTD`, `Proj`, `${S1}→${S2}`].map((h, i) => (
                <TableCell key={i} align={i > 1 ? 'right' : 'left'} sx={{ fontSize: '0.58rem', fontWeight: 700, textTransform: 'uppercase', color: accent, background: 'transparent' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((a, i) => (
              <TableRow key={i} hover sx={{ '&:hover td': { background: accent + '08' } }}>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{a.name}</TableCell>
                <TableCell><SegBadge seg={a.seg} /></TableCell>
                <TableCell align='right' sx={{ fontSize: '0.72rem', color: '#9a9ab0' }}>{nf(a.apr)}</TableCell>
                <TableCell align='right' sx={{ fontSize: '0.72rem' }}>{nf(a.may)}</TableCell>
                <TableCell align='right' sx={{ fontSize: '0.72rem' }}>{nf(a.jun)}</TableCell>
                <TableCell align='right' sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{nf(a.proj)}</TableCell>
                <TableCell align='right'><Pct v={a.gMJ} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    )
  }
  return (
    <Box sx={{ px: 3, pb: 2.5, pt: 1.5, background: '#F9FAFB', borderTop: `1px solid ${PRIMARY_LIGHT}` }}>
      {mini('Top growing brands',    s.growth, true)}
      {mini('Top de-growing brands', s.degr,   false)}
      {!s.growth?.length && !s.degr?.length && (
        <Typography sx={{ fontSize: '0.78rem', color: '#bbb', py: 1 }}>No account detail available.</Typography>
      )}
    </Box>
  )
}

export default function P4Sales({ vm, filters, L0, L1, L2, S0, S1, S2 }) {
  const [open,    setOpen]    = useState(new Set())
  const [sortCol, setSortCol] = useState('may')
  const [sortDir, setSortDir] = useState('desc')

  const sc = useMemo(() => scopeTotals(vm, filters), [vm, filters])

  const salesList = useMemo(() => {
    const store = vm.sales || {}
    let arr = (filters.seg === 'ENT' ? store.ent : filters.seg === 'SME' ? store.sme : store.all) || []
    if (filters.chips.has('behind')) arr = arr.filter(s => s.gMJ != null && s.gMJ < filters.goal)
    if (!sortCol) return arr
    return [...arr].sort((a, b) => { const va = a[sortCol] ?? -Infinity, vb = b[sortCol] ?? -Infinity; return sortDir === 'asc' ? va - vb : vb - va })
  }, [vm, filters, sortCol, sortDir])

  function toggleSort(col) { if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortCol(col); setSortDir('desc') } }
  function toggleOpen(name) { setOpen(prev => { const n = new Set(prev); n.has(name) ? n.delete(name) : n.add(name); return n }) }

  const thP = { sortCol, sortDir, onSort: toggleSort }

  return (
    <Box>
      <Grid container spacing={1.75} sx={{ mb: 2.5 }}>
        {[
          { label: 'Sales leaders',     value: nf(salesList.length), sub: filters.seg === 'ALL' ? 'all segments' : filters.seg, color: PRIMARY, icon: PeopleAltOutlinedIcon },
          { label: 'Total accounts',    value: nf(sc.accts),         sub: `${nf(sc.active)} active`, color: '#555' },
          { label: 'Inactive accounts', value: nf(sc.inactive),      sub: `no ${L2} shipments`,      color: RED,    icon: WarningAmberIcon },
          { label: `${L2} DRR`,         value: nf(sc.drrJun),        sub: <Pct v={sc.gMJ} />,        color: GREEN,  icon: SpeedIcon },
        ].map((k, i) => (
          <Grid item xs={6} sm={3} key={i}><Kpi {...k} /></Grid>
        ))}
      </Grid>

      <Box sx={{ mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Box sx={{ width: 4, height: 16, borderRadius: 1, background: PRIMARY }} />
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1a1a2e' }}>Sales Leadership</Typography>
          <Chip label={`${salesList.length} leaders`} size='small' sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, background: PRIMARY_LIGHT, color: PRIMARY, ml: 0.5 }} />
        </Box>
        <Typography sx={{ fontSize: '0.7rem', color: '#9a9ab0', ml: 1.25 }}>
          Click any row to expand top growing &amp; de-growing brands for that sales leader.
        </Typography>
      </Box>

      <Box sx={{ background: '#fff', borderRadius: '12px', border: '1px solid rgba(145,158,171,0.24)', boxShadow: 'none', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 580 }}>
          <Table size='small' stickyHeader>
            <TableHead>
              <TableRow>
                <Th {...thP}                  minW={170}>Sales Leader</Th>
                <Th {...thP} col='accts'    right>Accts</Th>
                <Th {...thP} col='active'   right>Active</Th>
                <Th {...thP} col='inactive' right>Inactive</Th>
                <Th {...thP} col='apr'      right>{L0}</Th>
                <Th {...thP} col='may'      right>{L1}</Th>
                <Th {...thP} col='jun'      right>{L2} MTD</Th>
                <Th {...thP} col='proj'     right>Proj {L2}</Th>
                <Th {...thP} col='gMJ'      right>{S1}→{S2}</Th>
                <Th right>vs Goal</Th>
              </TableRow>
            </TableHead>
            <TableBody>
              {salesList.map(s => {
                const isOpen = open.has(s.name)
                const rowBg  = s.gMJ < 0 ? '#fff8f8' : s.gMJ != null && s.gMJ < 10 ? '#fffdf0' : 'inherit'
                return (
                  <React.Fragment key={s.name}>
                    <TableRow onClick={() => toggleOpen(s.name)} sx={{ background: rowBg, cursor: 'pointer', '&:hover td': { background: '#F4F6F8' }, '& td': { transition: 'background 0.15s' } }}>
                      <TableCell sx={{ py: 1.1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box sx={{ width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: isOpen ? PRIMARY : PRIMARY_LIGHT, transition: 'background 0.15s' }}>
                            <KeyboardArrowRightIcon sx={{ fontSize: 15, color: isOpen ? '#fff' : PRIMARY, transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }} />
                          </Box>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', color: '#1a1a2e' }}>{s.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align='right' sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{nf(s.accts)}</TableCell>
                      <TableCell align='right'>
                        <Chip label={nf(s.active)} size='small' sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, background: '#dcfce7', color: '#166534', borderRadius: '6px' }} />
                      </TableCell>
                      <TableCell align='right'>
                        <Chip label={nf(s.inactive)} size='small' sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, background: '#f3f4f6', color: '#9ca3af', borderRadius: '6px' }} />
                      </TableCell>
                      <TableCell align='right' sx={{ fontSize: '0.72rem', color: '#bbb' }}>{nf(s.apr)}</TableCell>
                      <TableCell align='right' sx={{ fontSize: '0.78rem' }}>{nf(s.may)}</TableCell>
                      <TableCell align='right' sx={{ fontSize: '0.82rem', fontWeight: 700, color: PRIMARY }}>{nf(s.jun)}</TableCell>
                      <TableCell align='right' sx={{ fontSize: '0.72rem', color: '#9a9ab0' }}>{nf(s.proj)}</TableCell>
                      <TableCell align='right'><Pct v={s.gMJ} /></TableCell>
                      <TableCell align='right'><GoalChip g={s.gMJ} /></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={10} sx={{ p: 0, border: 0 }}>
                        <Collapse in={isOpen} unmountOnExit>
                          <DrillContent s={s} L0={L0} L1={L1} L2={L2} S1={S1} S2={S2} />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                )
              })}
              {salesList.length === 0 && (
                <TableRow><TableCell colSpan={10} align='center' sx={{ py: 5, color: '#bbb', fontSize: '0.82rem' }}>No sales leaders for this filter.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  )
}
